import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { TagIcon, PlusIcon } from '@heroicons/react/24/outline';
import inventoryApi from '../../../services/inventoryApi';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../Common/LoadingSpinner';
import { ProductFilters, EMPTY_PRODUCT, productParams, money, fmtDate, today } from '../Reports/shared/ReportKit';

// Price levels (RETAIL, WHOLESALE, STAFF …) and the price of each item on each level, from a date
const PriceLevels = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(['INVENTORY:PRICING:CREATE', 'INVENTORY:PRICING:UPDATE']);
  const [levels, setLevels] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [newLevel, setNewLevel] = useState(null); // { code, name, description }
  const [editLevel, setEditLevel] = useState(null);
  const [product, setProduct] = useState(EMPTY_PRODUCT);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [grid, setGrid] = useState({ levels: [], rows: [] });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null); // { itemId, itemCode, levelId, levelCode, price, effectiveFrom }
  const [saving, setSaving] = useState(false);

  const loadLevels = useCallback(() => {
    inventoryApi.getPriceLevels(showInactive ? { includeInactive: 'true' } : {}).then(r => setLevels(r?.data || [])).catch(() => toast.error('Failed to load price levels'));
  }, [showInactive]);
  const loadGrid = useCallback(async () => {
    setLoading(true);
    try {
      const r = await inventoryApi.getItemPrices({ ...productParams(product), ...(search ? { search } : {}), page, limit: 50 });
      setGrid(r?.data || { levels: [], rows: [] });
      setPagination(r?.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load item prices');
    } finally { setLoading(false); }
  }, [product, search, page]);

  useEffect(() => { loadLevels(); }, [loadLevels]);
  useEffect(() => { const t = setTimeout(loadGrid, 250); return () => clearTimeout(t); }, [loadGrid]);
  useEffect(() => { setPage(1); }, [product, search]);

  const saveLevel = async (isNew) => {
    const body = isNew ? newLevel : editLevel;
    try {
      const r = isNew ? await inventoryApi.createPriceLevel(body) : await inventoryApi.updatePriceLevel(body._id, { name: body.name, description: body.description, isActive: body.isActive });
      toast.success(r.message || 'Saved');
      isNew ? setNewLevel(null) : setEditLevel(null);
      loadLevels(); loadGrid();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save price level'); }
  };
  const savePrice = async () => {
    setSaving(true);
    try {
      const r = await inventoryApi.setItemPrice({ itemId: edit.itemId, levelId: edit.levelId, price: edit.price, effectiveFrom: edit.effectiveFrom });
      toast.success(r.message || 'Price saved');
      setEdit(null); loadGrid(); loadLevels();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save the price'); } finally { setSaving(false); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-5 py-3 flex items-center gap-3 rounded-t-xl">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><TagIcon className="w-4 h-4 text-white" /></div>
          <div className="flex-1"><h2 className="text-sm font-semibold text-white">Price Levels</h2><p className="text-indigo-200 text-xs">Price levels and the price of each item on each level — changes take effect from the date you choose</p></div>
          {canEdit && <button type="button" onClick={() => setNewLevel({ code: '', name: '', description: '' })} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-white text-indigo-700 rounded-md hover:bg-indigo-50"><PlusIcon className="w-4 h-4" />New Price Level</button>}
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Levels</span>
            <label className="text-xs text-gray-500 flex items-center gap-1"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive</label>
          </div>
          {levels.length === 0 ? <p className="text-sm text-gray-400 italic">No price levels yet{canEdit ? ' — click "New Price Level" (e.g. RETAIL, WHOLESALE, STAFF).' : '.'}</p> : (
            <div className="flex flex-wrap gap-2">
              {levels.map(l => (
                <div key={l._id} className={`border rounded-lg px-3 py-2 text-sm ${l.isActive ? 'border-gray-200' : 'border-dashed border-gray-300 opacity-60'}`}>
                  <div className="flex items-center gap-2"><span className="font-mono font-semibold text-indigo-700">{l.code}</span><span className="text-gray-700">{l.name}</span>
                    {canEdit && <button type="button" onClick={() => setEditLevel({ ...l })} className="text-xs text-gray-400 hover:text-indigo-600">Edit</button>}</div>
                  <div className="text-xs text-gray-400">{l.itemsPriced} item{l.itemsPriced === 1 ? '' : 's'} priced{l.isActive ? '' : ' · inactive'}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Item price grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-4 py-3 flex flex-wrap items-end gap-4 border-b border-gray-100">
          <ProductFilters value={product} onChange={setProduct} />
          <div className="flex flex-col gap-1"><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Search</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Code or name…" className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 w-[200px]" /></div>
          <div className="flex-1" />
          <span className="text-xs text-gray-500">{pagination.total} items</span>
        </div>
        {loading ? <div className="py-10 flex justify-center"><LoadingSpinner inline size="lg" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500"><tr>
                <th className="text-left px-4 py-2 font-medium">Item Code</th><th className="text-left px-4 py-2 font-medium">Item Name</th>
                <th className="text-right px-4 py-2 font-medium">Cost</th><th className="text-right px-4 py-2 font-medium">Selling Price</th>
                {grid.levels.map(l => <th key={l._id} className="text-right px-4 py-2 font-semibold text-indigo-700">{l.code}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {grid.rows.length === 0 && <tr><td colSpan={4 + grid.levels.length} className="px-4 py-8 text-center text-gray-400 italic">No items match</td></tr>}
                {grid.rows.map(r => (
                  <tr key={r.itemId} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{r.itemCode}</td>
                    <td className="px-4 py-2">{r.itemName}<div className="text-xs text-gray-400">{[r.groupName, r.typeName].filter(Boolean).join(' › ')}</div></td>
                    <td className="px-4 py-2 text-right font-mono text-gray-500">{money(r.cost)}</td>
                    <td className="px-4 py-2 text-right font-mono text-gray-500">{money(r.sellingPrice)}</td>
                    {grid.levels.map(l => {
                      const p = r.prices[l.code] || {};
                      return (
                        <td key={l._id} className="px-4 py-2 text-right">
                          <button type="button" disabled={!canEdit}
                            onClick={() => setEdit({ itemId: r.itemId, itemCode: r.itemCode, itemName: r.itemName, levelId: l._id, levelCode: l.code, price: p.price ?? '', effectiveFrom: today(), current: p })}
                            className={`font-mono ${p.price != null ? 'font-semibold text-gray-900' : 'text-gray-300'} ${canEdit ? 'hover:text-indigo-600 hover:underline' : ''}`}
                            title={p.effectiveFrom ? `From ${fmtDate(p.effectiveFrom)}` : 'Set price'}>
                            {p.price != null ? money(p.price) : (canEdit ? 'Set' : '—')}
                          </button>
                          {p.scheduled && <div className="text-[10px] text-amber-700" title={`From ${fmtDate(p.scheduled.effectiveFrom)}`}>→ {money(p.scheduled.price)} from {fmtDate(p.scheduled.effectiveFrom)}</div>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination.pages > 1 && (
          <div className="px-4 py-2 border-t border-gray-100 flex justify-end items-center gap-2 text-xs">
            <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 rounded border disabled:opacity-40">Prev</button>
            <span>Page {pagination.page} of {pagination.pages}</span>
            <button type="button" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 rounded border disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      {/* New / edit level */}
      {(newLevel || editLevel) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">{newLevel ? 'New Price Level' : `Edit ${editLevel.code}`}</h3>
            {newLevel && <div><label className="block text-xs font-medium text-gray-700 mb-1">Code <span className="text-red-500">*</span></label>
              <input value={newLevel.code} onChange={(e) => setNewLevel(v => ({ ...v, code: e.target.value.toUpperCase() }))} placeholder="e.g. RETAIL" className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm font-mono" /></div>}
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
              <input value={(newLevel || editLevel).name} onChange={(e) => (newLevel ? setNewLevel : setEditLevel)(v => ({ ...v, name: e.target.value }))} placeholder="e.g. Retail price" className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <input value={(newLevel || editLevel).description || ''} onChange={(e) => (newLevel ? setNewLevel : setEditLevel)(v => ({ ...v, description: e.target.value }))} className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" /></div>
            {editLevel && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editLevel.isActive} onChange={(e) => setEditLevel(v => ({ ...v, isActive: e.target.checked }))} /> Active</label>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setNewLevel(null); setEditLevel(null); }} className="px-4 py-1.5 text-sm border border-gray-300 rounded-md">Cancel</button>
              <button type="button" onClick={() => saveLevel(!!newLevel)} className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Set price */}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">{edit.levelCode} price — <span className="font-mono">{edit.itemCode}</span></h3>
            <p className="text-xs text-gray-500">{edit.itemName}{edit.current?.price != null && <> · current {money(edit.current.price)} from {fmtDate(edit.current.effectiveFrom)}</>}</p>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Price (Rs) <span className="text-red-500">*</span></label>
              <input type="number" min="0" step="0.01" autoFocus value={edit.price} onChange={(e) => setEdit(v => ({ ...v, price: e.target.value }))} className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm text-right font-mono" /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Effective from</label>
              <input type="date" value={edit.effectiveFrom} onChange={(e) => setEdit(v => ({ ...v, effectiveFrom: e.target.value }))} className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
              <p className="text-xs text-gray-400 mt-0.5">The previous price stays in force until this date.</p></div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)} className="px-4 py-1.5 text-sm border border-gray-300 rounded-md">Cancel</button>
              <button type="button" disabled={saving || edit.price === ''} onClick={savePrice} className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save price'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceLevels;
